import { useState } from 'react';
import styled from 'styled-components';
import DeviceServiceModal from '../../../../components/ui/modal/DeviceServiceModal';
import { useQuery } from '@tanstack/react-query';
import { planApis } from '../../../../shared/apis/plan/apis';
import { handleError } from '../../../../components/ui/err/handleError';
import { AxiosError } from 'axios';
import { AxiosCommonError } from '../../../../shared/types';
import TableUnit from '../../../../components/units/Management/DeviceService/TableUnit';
import { useSearchParams } from 'react-router-dom';
import * as C from '../../../../components/ui/common/CommonStyles';
import { Button, Group, Pagination } from '@mantine/core';
import CustomSearch from '../../../../components/ui/search/customSearch';
import { useCustomSearch } from '../../../../shared/hook/useCustomSearch';

const options = [
  { value: 'all', label: '전체' },
  { value: '정기결제', label: '구독 결제' },
  { value: '단건결제', label: '단건 결제' },
];

export interface IPlan {
  id: string;
  title: string;
  alias: string;
  payInterval: number;
  description: string;
}

export interface IDeviceService {
  id: string;
  mac: string;
  deviceUsername: string;
  deviceType: string;
  freeTrialPeriod: number;
  subPlan: IPlan[];
  singlePlan: IPlan[];
  curPlan: IPlan;
  curPlanStartDate: string;
  curPlanEndDate: string;
  regDate: string;
  regUsername: string;
}

const ServiceManagement = () => {
  let total = 0;
  const initSearchType = 'all';
  const [isOpen, setIsOpen] = useState(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [editItem, setEditItem] = useState<IDeviceService | null>(null);

  // 클라이언트단 url parameter 설정
  const [searchParams, setSearchParams] = useSearchParams('');
  const searchWord = searchParams.get('keyword') || '';
  const searchType = searchParams.get('searchType') || initSearchType;
  const page = searchParams.get('page') || '1';
  const size = searchParams.get('size') || '10';

  const { inputRef, handleSearch, resetWord, handleSearchType, resetSearchInput } = useCustomSearch({
    initType: initSearchType,
  });

  const openModal = () => {
    setIsOpen(true);
  };

  const { data: deviceServiceList, refetch } = useQuery(
    ['getDeviceServiceList', page, size, searchType, searchWord],
    () =>
      planApis.getDeviceServiceList({
        page: Number(page),
        size: Number(size),
        searchType: searchType === 'all' ? '' : searchType,
        search: searchWord,
      }),
    {
      select: (data) => {
        total = data.data.total;
        return data.data.result;
      },
      onError: (error: AxiosError<AxiosCommonError>) => {
        handleError(error);
      },
    },
  );

  const renderAvailablePlans = (service: IDeviceService) => {
    const { subPlan, singlePlan } = service;

    const totalPlans = subPlan.length + singlePlan.length;

    if (totalPlans === 0) {
      return '-';
    }

    const primaryPlan = subPlan.length > 0 ? subPlan[0].title : singlePlan[0].title;
    const additionalPlans = totalPlans > 1 ? ` 외 ${totalPlans - 1}` : '';

    return primaryPlan + additionalPlans;
  };

  const onClickService = (service: IDeviceService) => {
    return () => {
      setEditItem({ ...service });
      setIsEdit(true);
      openModal();
    };
  };

  return (
    <div>
      <C.TitleBox>
        <div className="title">디바이스 서비스 정보 등록</div>
      </C.TitleBox>
      <C.ListBox>
        <C.Toolbar>
          <CustomSearch
            resetSearch={resetWord}
            resetSearchInput={resetSearchInput}
            searchType={searchType}
            setSearchType={handleSearchType}
            inputRef={inputRef}
            handleSearch={handleSearch}
            onClickSearch={(e: React.MouseEvent<HTMLButtonElement>) => {
              handleSearch(e);
            }}
            searchOptions={options}
            placeholder="MAC, 고객 ID로 검색해주세요."
          />
          <RegBox>
            <Button color="secondary.5" onClick={openModal}>
              신규 등록
            </Button>
          </RegBox>
        </C.Toolbar>
        <C.TableWrap>
          <TableUnit data={deviceServiceList} renderAvailablePlans={renderAvailablePlans} onClickService={onClickService} />
          {total > 0 ? (
            <Group justify="center" my="md">
              <Pagination
                total={Math.ceil(total / Number(size))}
                value={Number(page)}
                onChange={(value) => {
                  setSearchParams({
                    page: String(value),
                    size,
                    searchType: searchType,
                    keyword: searchWord,
                  });
                }}
              />
            </Group>
          ) : null}
        </C.TableWrap>
      </C.ListBox>
      <DeviceServiceModal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        isEdit={isEdit}
        setIsEdit={setIsEdit}
        editItem={editItem}
        setEditItem={setEditItem}
        refetch={refetch}
      />
    </div>
  );
};

export default ServiceManagement;

const RegBox = styled.div`
  button {
    width: 110px;
    height: 40px;
    /* background-color: gray;
    border-color: gray; */
  }
`;
